// feature-module/inventory/TaxList.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Table from "../../core/pagination/datatable";
import AddTax from "../../core/modals/inventory/addtax";
import EditTax from "../../core/modals/inventory/edittax";
import { userActions } from "../../core/action/userAction";
import Swal from "sweetalert2";
import {
  Calendar,
  ChevronUp,
  Filter,
  PlusCircle,
  RotateCcw,
  Sliders,
  Zap,
  Eye,
} from "feather-icons-react/build/IconComponents";
import Select from "react-select";
import { DatePicker } from "antd";
import withReactContent from "sweetalert2-react-content";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import { setToogleHeader } from "../../core/redux/action";
import { taxActions } from "../../core/action/taxaction";
import moment from "moment";

const MySwal = withReactContent(Swal);

const TaxList = () => {
  const dataSource = useSelector((state) => state.taxRates || []);
  const dispatch = useDispatch();
  const data = useSelector((state) => state.toggle_header);

  // Get user role for super admin check
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = user.role_slug === 'super-admin';

  // Permission checks
  const canCreate = userActions.hasPermission('create-taxes');
  const canEdit = userActions.hasPermission('edit-taxes');
  const canDelete = userActions.hasPermission('delete-taxes');
  const canView = userActions.hasPermission('view-taxes');

  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    tax_code: '',
    date: ''
  });
  const [selectedDate, setSelectedDate] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1
  });
  const [sortConfig, setSortConfig] = useState({ value: 'newest', label: 'Newest' });

  // Filter taxes client-side based on search term
  const getFilteredTaxes = React.useMemo(() => {
    if (!searchTerm || !dataSource || dataSource.length === 0) {
      return dataSource;
    }

    const searchLower = searchTerm.toLowerCase().trim();

    return dataSource.filter(tax => {
      const name = (tax.tax_name || tax.name || '').toLowerCase();
      const code = (tax.tax_code || '').toLowerCase();
      const description = (tax.description || '').toLowerCase();

      // Exact name or code match has highest priority
      if (name === searchLower || code === searchLower) return true;

      // Name or code starts with search term
      if (name.startsWith(searchLower) || code.startsWith(searchLower)) return true;

      // Description contains search term
      if (description.includes(searchLower)) return true;

      return false;
    });
  }, [searchTerm, dataSource]);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedTax, setSelectedTax] = useState(null);

  const oldandlatestvalue = [
    { value: "date", label: "Sort by Date" },
    { value: "newest", label: "Newest" },
    { value: "oldest", label: "Oldest" },
  ];

  const taxCodeOptions = [
    { value: "", label: "All Tax Codes" },
    { value: "CGST", label: "CGST" },
    { value: "SGST", label: "SGST" },
    { value: "IGST", label: "IGST" },
    { value: "VAT", label: "VAT" },
  ];

  // Fetch taxes
  const fetchTaxes = useCallback(async () => {
    // Check if user has view permission
    if (!canView && !isSuperAdmin) {
      console.warn('User does not have permission to view taxes');
      return;
    }
    
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm,
        tax_code: filters.tax_code,
        date: filters.date,
        sort_by: sortConfig.value === 'date' ? 'created_at' : 'created_at',
        sort_order: sortConfig.value === 'oldest' ? 'ASC' : 'DESC'
      };

      const result = await dispatch(taxActions.fetchTaxes(params));
      
      if (result.success && result.data) {
        setPagination(prev => ({
          ...prev,
          total: result.data.pagination?.total || 0,
          totalPages: result.data.pagination?.totalPages || 1
        }));
      }
    } catch (error) {
      console.error('Error fetching taxes:', error);
      MySwal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Failed to fetch tax rates',
        timer: 2000
      });
    } finally {
      setLoading(false);
    }
  }, [dispatch, pagination.page, pagination.limit, searchTerm, filters.tax_code, filters.date, sortConfig, canView, isSuperAdmin]);

  // Handle view tax
  const handleViewTax = (tax) => {
    if (!canView && !isSuperAdmin) {
      MySwal.fire({
        title: 'Access Denied',
        text: 'You do not have permission to view tax details',
        icon: 'error',
        timer: 3000
      });
      return;
    }
    setSelectedTax(tax);
    setShowViewModal(true);
  };

  // Handle search
  const handleSearch = (e) => {
    if (e.key === 'Enter' || e.type === 'click') {
      setPagination(prev => ({ ...prev, page: 1 }));
      setTimeout(() => fetchTaxes(), 0);
    }
  };

  // Handle filter change
  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  // Handle date change
  const handleDateChange = (date) => {
    setSelectedDate(date);
    if (date) {
      handleFilterChange('date', moment(date).format('YYYY-MM-DD'));
    } else {
      handleFilterChange('date', '');
    }
  };

  // Apply filters
  const handleApplyFilters = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    setTimeout(() => fetchTaxes(), 0);
    setIsFilterVisible(false);
  };

  // Clear filters
  const handleClearFilters = () => {
    setFilters({
      tax_code: '',
      date: ''
    });
    setSelectedDate(null);
    setSearchTerm('');
    setPagination(prev => ({ ...prev, page: 1 }));
    setTimeout(() => fetchTaxes(), 0);
  };

  // Handle sort change
  const handleSortChange = (option) => {
    setSortConfig(option);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Handle delete tax
  const handleDeleteTax = (id, taxName) => {
    if (!canDelete && !isSuperAdmin) {
      MySwal.fire({
        title: 'Access Denied',
        text: 'You do not have permission to delete taxes',
        icon: 'error',
        timer: 3000
      });
      return;
    }
    
    MySwal.fire({
      title: 'Are you sure?',
      text: `You want to delete ${taxName}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonColor: '#3085d6',
      cancelButtonText: 'Cancel',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await dispatch(taxActions.deleteTax(id));
          if (response && response.success) {
            MySwal.fire({
              icon: 'success',
              title: 'Deleted!',
              text: 'Tax rate has been deleted.',
              timer: 2000,
              showConfirmButton: false
            });
            fetchTaxes();
          } else {
            throw new Error(response?.message || 'Delete failed');
          }
        } catch (error) {
          MySwal.fire({
            icon: 'error',
            title: 'Error!',
            text: error.message || 'Failed to delete tax rate',
            confirmButtonColor: '#ff0000',
          });
        }
      }
    });
  };

  // Handle edit tax
  const handleEditTax = (tax) => {
    if (!canEdit && !isSuperAdmin) {
      MySwal.fire({
        title: 'Access Denied',
        text: 'You do not have permission to edit taxes',
        icon: 'error',
        timer: 3000
      });
      return;
    }
    setSelectedTax(tax);
    setShowEditModal(true);
  };

  // Handle add tax
  const handleAddTax = () => {
    if (!canCreate && !isSuperAdmin) {
      MySwal.fire({
        title: 'Access Denied',
        text: 'You do not have permission to create taxes',
        icon: 'error',
        timer: 3000
      });
      return;
    }
    setShowAddModal(true);
  };

  // Handle modal close
  const handleAddModalClose = () => {
    setShowAddModal(false);
  };

  const handleEditModalClose = () => {
    setShowEditModal(false);
    setSelectedTax(null);
  };

  const handleViewModalClose = () => {
    setShowViewModal(false);
    setSelectedTax(null);
  };

  // Handle success after add/edit
  const handleSuccess = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setSelectedTax(null);
    fetchTaxes();
  };

  // Handle refresh
  const handleRefresh = () => {
    fetchTaxes();
  };

  // Handle page change
  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, page }));
  };

  // Handle limit change
  const handleLimitChange = (e) => {
    setPagination(prev => ({ 
      ...prev, 
      limit: parseInt(e.target.value), 
      page: 1 
    }));
  };

  // Table columns
  const columns = [
    {
      title: "S.No",
      key: "sno",
      width: "60px",
      render: (text, record, index) => (
        <span>{(pagination.page - 1) * pagination.limit + index + 1}</span>
      ),
    },
    {
      title: "Tax Name",
      dataIndex: "tax_name",
      key: "tax_name",
      sorter: true,
    },
    {
      title: "Tax Code",
      dataIndex: "tax_code",
      key: "tax_code",
      sorter: true,
    },
    {
      title: "Tax Rate (%)",
      dataIndex: "tax_rate",
      key: "tax_rate",
      sorter: true,
      render: (text) => <span className="badge bg-info">{text}%</span>,
    },
    {
      title: "Created On",
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => text ? moment(text).format('DD-MM-YYYY') : '-',
      sorter: true,
    },
    {
      title: "Actions",
      key: "actions",
      width: "120px",
      render: (_, record) => (
        <div className="edit-delete-action">
          {/* View Button - Show if user has view permission */}
          {(canView || isSuperAdmin) && (
            <button 
              className="btn btn-sm btn-outline-info me-2 p-2" 
              onClick={() => handleViewTax(record)}
              title="View Details"
            >
              <Eye size={14} />
            </button>
          )}
          
          {/* Edit Button - Show if user has edit permission */}
          {(canEdit || isSuperAdmin) && (
            <Link
              className="me-2 p-2"
              to="#"
              onClick={() => handleEditTax(record)}
              title="Edit"
            >
              <i data-feather="edit" className="feather-edit"></i>
            </Link>
          )}
          
          {/* Delete Button - Show if user has delete permission */}
          {(canDelete || isSuperAdmin) && (
            <Link 
              className="confirm-text p-2" 
              to="#"
              onClick={() => handleDeleteTax(record.id, record.tax_name)}
              title="Delete"
            >
              <i data-feather="trash-2" className="feather-trash-2"></i>
            </Link>
          )}
        </div>
      ),
    },
  ];

  // Initial load - Moved hooks to top level (fixed conditional hooks error)
  useEffect(() => {
    if (canView || isSuperAdmin) {
      fetchTaxes();
    }
  }, []); // Empty dependency array

  // Update when sort, page, or limit changes
  useEffect(() => {
    if (canView || isSuperAdmin) {
      fetchTaxes();
    }
  }, [sortConfig, pagination.page, pagination.limit]);

  const renderTooltip = (props, text) => (
    <Tooltip id={`tooltip-${text}`} {...props}>{text}</Tooltip>
  );

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>Tax Rates</h4>
                <h6>Manage your tax rates</h6>
              </div>
            </div>
            <ul className="table-top-head">
              <li>
                <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'PDF')}>
                  <Link to="#">
                    <ImageWithBasePath src="assets/img/icons/pdf.svg" alt="img" />
                  </Link>
                </OverlayTrigger>
              </li>
              <li>
                <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Excel')}>
                  <Link to="#">
                    <ImageWithBasePath src="assets/img/icons/excel.svg" alt="img" />
                  </Link>
                </OverlayTrigger>
              </li>
              <li>
                <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Print')}>
                  <Link to="#" onClick={() => window.print()}>
                    <i data-feather="printer" className="feather-printer" />
                  </Link>
                </OverlayTrigger>
              </li>
              <li>
                <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Refresh')}>
                  <Link 
                    to="#"
                    onClick={handleRefresh}
                  >
                    <RotateCcw />
                  </Link>
                </OverlayTrigger>
              </li>
              <li>
                <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Collapse')}>
                  <Link
                    to="#"
                    id="collapse-header"
                    className={data ? "active" : ""}
                    onClick={() => dispatch(setToogleHeader(!data))}
                  >
                    <ChevronUp />
                  </Link>
                </OverlayTrigger>
              </li>
            </ul>
            
            {/* Add New Tax Button - Only show if user has create permission */}
            {(canCreate || isSuperAdmin) && (
              <div className="page-btn">
                <Link
                  to="#"
                  className="btn btn-added"
                  onClick={handleAddTax}
                >
                  <PlusCircle className="me-2" />
                  Add New Tax Rate
                </Link>
              </div>
            )}
          </div>
          
          {/* Tax List */}
          <div className="card table-list-card">
            <div className="card-body">
              {/* Only show filters and search if user has view permission */}
              {(canView || isSuperAdmin) && (
                <>
                  <div className="table-top">
                    <div className="search-set">
                      <div className="search-input">
                        <input
                          type="text"
                          placeholder="Search by tax name or code"
                          className="form-control form-control-sm formsearch"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleSearch(e)}
                        />
                        <Link 
                          to="#" 
                          className="btn btn-searchset"
                          onClick={handleSearch}
                        >
                          <i data-feather="search" className="feather-search" />
                        </Link>
                      </div>
                    </div>
                    <div className="search-path">
                      <Link 
                        to="#"
                        className={`btn btn-filter ${isFilterVisible ? "setclose" : ""}`} 
                        onClick={() => setIsFilterVisible(!isFilterVisible)}
                      >
                        <Filter className="filter-icon" />
                        <span>
                          <ImageWithBasePath src="assets/img/icons/closes.svg" alt="img" />
                        </span>
                      </Link>
                    </div>
                    <div className="form-sort">
                      <Sliders className="info-img" />
                      <Select
                        className="select"
                        options={oldandlatestvalue}
                        value={sortConfig}
                        onChange={handleSortChange}
                        placeholder="Sort by"
                      />
                    </div>
                  </div>
                  
                  {/* Filter Section */}
                  <div
                    className={`card${isFilterVisible ? " visible" : ""}`}
                    id="filter_inputs"
                    style={{ display: isFilterVisible ? "block" : "none" }}
                  >
                    <div className="card-body pb-0">
                      <div className="row">
                        <div className="col-lg-4 col-sm-6 col-12">
                          <div className="input-blocks">
                            <Zap className="info-img" />
                            <Select
                              className="select"
                              options={taxCodeOptions}
                              placeholder="Select Tax Code"
                              value={taxCodeOptions.find(opt => opt.value === filters.tax_code)}
                              onChange={(selected) => handleFilterChange('tax_code', selected?.value || '')}
                            />
                          </div>
                        </div>
                        <div className="col-lg-4 col-sm-6 col-12">
                          <div className="input-blocks">
                            <Calendar className="info-img" />
                            <div className="input-groupicon">
                              <DatePicker
                                value={selectedDate}
                                onChange={handleDateChange}
                                className="form-control"
                                format="DD-MM-YYYY"
                                placeholder="Select Date"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="col-lg-4 col-sm-6 col-12 ms-auto">
                          <div className="input-blocks d-flex gap-2">
                            <button 
                              className="btn btn-filters"
                              onClick={handleApplyFilters}
                            >
                              <i data-feather="search" className="feather-search" /> Apply Filters
                            </button>
                            <button 
                              className="btn btn-secondary"
                              onClick={handleClearFilters}
                            >
                              Clear
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
              
              {/* Tax Table */}
              <div className="table-responsive">
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2 text-muted">Loading tax rates...</p>
                  </div>
                ) : !canView && !isSuperAdmin ? (
                  <div className="text-center py-5">
                    <i data-feather="lock" className="feather-lock" style={{ fontSize: '48px', opacity: 0.5 }} />
                    <h5 className="mt-3">Access Denied</h5>
                    <p className="text-muted">You dont have permission to view tax rates</p>
                  </div>
                ) : getFilteredTaxes.length === 0 ? (
                  <div className="text-center py-5">
                    <i data-feather="percent" className="feather-percent" style={{ fontSize: '48px', opacity: 0.5 }} />
                    <h5 className="mt-3">No tax rates found</h5>
                    <p className="text-muted">
                      {searchTerm || filters.tax_code || filters.date
                        ? "Try adjusting your search or filters"
                        : "Add your first tax rate to get started"}
                    </p>
                    {(canCreate || isSuperAdmin) && (
                      <button
                        className="btn btn-primary mt-3"
                        onClick={handleAddTax}
                      >
                        <PlusCircle size={16} className="me-2" />
                        Add New Tax Rate
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    <Table columns={columns} dataSource={getFilteredTaxes} rowKey="id" />
                    
                    {/* Pagination */}
                    {pagination.totalPages > 1 && (
                      <div className="pagination-wrapper mt-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="pagination-info text-muted">
                            Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                            {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} entries
                          </div>
                          <div className="pagination-controls d-flex align-items-center">
                            <select 
                              className="form-select form-select-sm me-2"
                              value={pagination.limit}
                              onChange={handleLimitChange}
                              style={{ width: 'auto' }}
                            >
                              <option value="10">10 per page</option>
                              <option value="20">20 per page</option>
                              <option value="50">50 per page</option>
                              <option value="100">100 per page</option>
                            </select>
                            
                            <nav>
                              <ul className="pagination pagination-sm mb-0">
                                <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                                  <button 
                                    className="page-link" 
                                    onClick={() => handlePageChange(pagination.page - 1)}
                                    disabled={pagination.page === 1}
                                  >
                                    Previous
                                  </button>
                                </li>
                                
                                {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                                  let pageNum;
                                  if (pagination.totalPages <= 5) {
                                    pageNum = i + 1;
                                  } else if (pagination.page <= 3) {
                                    pageNum = i + 1;
                                  } else if (pagination.page >= pagination.totalPages - 2) {
                                    pageNum = pagination.totalPages - 4 + i;
                                  } else {
                                    pageNum = pagination.page - 2 + i;
                                  }
                                  
                                  return (
                                    <li 
                                      key={pageNum} 
                                      className={`page-item ${pagination.page === pageNum ? 'active' : ''}`}
                                    >
                                      <button 
                                        className="page-link" 
                                        onClick={() => handlePageChange(pageNum)}
                                      >
                                        {pageNum}
                                      </button>
                                    </li>
                                  );
                                })}
                                
                                <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                                  <button 
                                    className="page-link" 
                                    onClick={() => handlePageChange(pagination.page + 1)}
                                    disabled={pagination.page === pagination.totalPages}
                                  >
                                    Next
                                  </button>
                                </li>
                              </ul>
                            </nav>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
          
          {/* Modals */}
          {showAddModal && (
            <AddTax 
              onClose={handleAddModalClose}
              onSuccess={handleSuccess}
            />
          )}
          
          {showEditModal && selectedTax && (
            <EditTax 
              tax={selectedTax}
              onClose={handleEditModalClose}
              onSuccess={handleSuccess}
            />
          )}
          
          {/* View Tax Modal */}
          {showViewModal && selectedTax && (
            <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={handleViewModalClose}>
              <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Tax Details</h5>
                    <button type="button" className="btn-close" onClick={handleViewModalClose}></button>
                  </div>
                  <div className="modal-body">
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label fw-bold">Tax Name:</label>
                        <p>{selectedTax.tax_name}</p>
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label fw-bold">Tax Code:</label>
                        <p>{selectedTax.tax_code}</p>
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label fw-bold">Tax Rate (%):</label>
                        <p><span className="badge bg-info">{selectedTax.tax_rate}%</span></p>
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label fw-bold">Status:</label>
                        <p>
                          <span className={`badge ${selectedTax.status === 'Active' ? 'bg-success' : 'bg-danger'}`}>
                            {selectedTax.status || 'Active'}
                          </span>
                        </p>
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label fw-bold">Created On:</label>
                        <p>{selectedTax.created_at ? moment(selectedTax.created_at).format('DD-MM-YYYY HH:mm:ss') : '-'}</p>
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label fw-bold">Last Updated:</label>
                        <p>{selectedTax.updated_at ? moment(selectedTax.updated_at).format('DD-MM-YYYY HH:mm:ss') : '-'}</p>
                      </div>
                      {selectedTax.description && (
                        <div className="col-md-12 mb-3">
                          <label className="form-label fw-bold">Description:</label>
                          <p>{selectedTax.description}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={handleViewModalClose}>
                      Close
                    </button>
                    {(canEdit || isSuperAdmin) && (
                      <button 
                        type="button" 
                        className="btn btn-primary"
                        onClick={() => {
                          handleViewModalClose();
                          handleEditTax(selectedTax);
                        }}
                      >
                        Edit Tax
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default TaxList;